import { Box, CircularProgress, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { TriggerType } from '@/types/dmAutomation';
import { useAutomationGuide } from '@/hooks/queries/dm-automation';
import { TRIGGER_TO_GUIDE_TYPE, type AutomationGuideStep } from '@/services/automationGuide.service';
import { ACCENT, ACCENT_TINT } from './_shared';
import TriggerExplainer from './TriggerExplainer';

interface Props {
    open: boolean;
    onClose: () => void;
    triggerType: TriggerType | null;
}

const GuideStepRow = ({ step }: { step: AutomationGuideStep }) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
            sx={{
                flexShrink: 0,
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: ACCENT,
                color: '#fff',
                fontSize: 12.5,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: '2px',
            }}
        >
            {step.no}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: 'text.primary' }}>{step.title}</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: '2px', lineHeight: 1.6 }}>
                {step.desc}
            </Typography>
            {step.image && (
                <Box sx={{ mt: 1.25, display: 'flex' }}>
                    <Box
                        component="img"
                        src={step.image}
                        alt=""
                        loading="lazy"
                        sx={{
                            width: '100%',
                            maxWidth: 300,
                            maxHeight: 520,
                            objectFit: 'contain',
                            borderRadius: 3,
                            border: '1px solid rgba(0,0,0,0.08)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                            display: 'block',
                        }}
                    />
                </Box>
            )}
        </Box>
    </Stack>
);

/**
 * 자동DM 자동화 설명서 팝업.
 * 서버(GET /automation-guides/{type})에서 받아 렌더하고,
 * 미구현(404)·오류 시 내장 문구(TriggerExplainer)로 폴백한다.
 */
const AutomationGuideDialog = ({ open, onClose, triggerType }: Props) => {
    const guideType = triggerType ? TRIGGER_TO_GUIDE_TYPE[triggerType] : null;
    // 서버에 해당 type 가이드가 있으면 사진 가이드로, 없으면(404) 내장 문구로 폴백.
    // story/dm 콘텐츠가 서버에 추가되는 즉시 자동으로 사진 가이드가 뜬다.
    const { data: guide, isLoading, isError } = useAutomationGuide(guideType, open);

    const useFallback = isError || (!isLoading && !guide);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '18px', m: 2 } }}
        >
            {/* 헤더 */}
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
                sx={{ p: { xs: '18px 20px 8px', sm: '22px 24px 10px' } }}
            >
                <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.4, pr: 1 }}>
                    {guide?.title ?? '자동화 설명서'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ mt: '-4px', mr: '-6px', color: 'text.secondary' }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Stack>

            <Box sx={{ px: { xs: '20px', sm: '24px' }, pb: { xs: '20px', sm: '24px' } }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : useFallback ? (
                    // 서버 미구현/오류 → 내장 문구
                    triggerType ? (
                        <TriggerExplainer type={triggerType} />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            준비 중인 안내입니다.
                        </Typography>
                    )
                ) : (
                    <>
                        {guide!.subtitle && (
                            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
                                {guide!.subtitle}
                            </Typography>
                        )}
                        <Stack spacing={2.25}>
                            {guide!.steps.map((s) => (
                                <GuideStepRow key={s.no} step={s} />
                            ))}
                        </Stack>
                        {guide!.goodFor && (
                            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: 2, bgcolor: ACCENT_TINT }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: ACCENT, mb: 0.5 }}>
                                    이런 경우에 잘 맞아요
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>
                                    {guide!.goodFor}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Dialog>
    );
};

export default AutomationGuideDialog;
